import React, { useEffect, useRef, useState } from "react";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import {Border, BtnContainer, DropZone, UploadFileContainer} from "./styles";
import { UploadFileProp } from "./types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Button } from "../Button";
import theme from "../../theme/theme";

const UploadFile: React.FC<UploadFileProp> = ({
  status = 0,
  changeFile,
  changeFiles,
  verified = false,
  setVerified = (verified: boolean) => {},
  uploadType = "ocr",
  imagePath = "/",
  uploadMany = false,
  selectedFiles: propSelectedFiles,
  setSelectedFiles: propSetSelectedFiles,
  customDescription = "Arrastra y suelta tus archivos para subirlos",
}) => {
  // --------------------- Refs ---------------------
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --------------------- States ---------------------
  const [selectedFiles, setSelectedFiles] = useState<File[]>(
    propSelectedFiles || []
  );
  const [amountFiles, setAmountFiles] = useState(0);

  const [uploaded, setUploaded] = useState(false);
  const [buttonIcon, setButtonIcon] = useState(true);

  // --------------------- Handlers ---------------------

  const handleOpenFileDialog = () => {
    const files = fileInputRef.current?.files;
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Abrir el explorador de archivos
      setSelectedFiles(files ? Array.from(files) : []);
      setAmountFiles(files ? files?.length : 0);
    }
  }; 
 
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file.type.includes("image")) {
      setSelectedFiles([file]);
      setAmountFiles(event.dataTransfer.files.length);
      changeFile?.(file);
      changeFiles?.([file]);
    } else {
      toast.error("Solo se aceptan archivos de imagen y pdf.");
    }
  };

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files: FileList | null = event.target.files;
    if (files && changeFile) {
      const selectedFilesArray: File[] = Array.from(files);
      const imageFiles: File[] = selectedFilesArray.filter((file) =>
        file.type.includes("image")
      );

      if (imageFiles.length > 0) {
        const croppedImages = await Promise.all(
          imageFiles.map(async (file) => {
            return new Promise<File>((resolve) => {
              const image = new Image();
              const reader = new FileReader();

              reader.readAsDataURL(file);

              reader.onload = (e) => {
                if (
                  e.target &&
                  e.target.result &&
                  typeof e.target.result === "string"
                ) {
                  image.src = e.target.result;

                  image.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    if (!ctx) return;

                    const targetWidth = (4 * image.height) / 3; // 4:3 aspect ratio
                    const xOffset = (image.width - targetWidth) / 2;

                    canvas.width = targetWidth;
                    canvas.height = image.height;
                    ctx.drawImage(
                      image,
                      xOffset,
                      0,
                      targetWidth,
                      image.height,
                      0,
                      0,
                      targetWidth,
                      image.height
                    );

                    canvas.toBlob((blob) => {
                      if (blob) {
                        const croppedFile = new File([blob], file.name, {
                          type: file.type,
                        });
                        resolve(croppedFile);
                      } else {
                        resolve(file); // Use the original file if blob is null
                      }
                    }, file.type);
                  };
                }
              };
            });
          })
        );

        setSelectedFiles(croppedImages);
        changeFile(croppedImages[0]);
        changeFiles?.(croppedImages);
      } else {
        toast.error("Solo se aceptan archivos de imagen y pdf.");
      }
    }
  };

  const handleUploadFileImgs = async () => {
    if (selectedFiles.length > 0) {
      // setVerified(true);
      changeFiles?.(selectedFiles);
    }
  };

  // --------------------- Effects ---------------------

  useEffect(() => {
    // Add an event listener to the file input element
    const inputElement = fileInputRef.current;
    if (inputElement) {
      inputElement.addEventListener("change", handleOpenFileDialog);
      return () => {
        // Remove the event listener when the component unmounts
        inputElement.removeEventListener("change", handleOpenFileDialog);
      };
    }
  }, []); // Empty dependency array to run this effect only once

  useEffect(() => {
    if (selectedFiles.length > 0) {
      // setUploaded(true);
      setButtonIcon(false);
      handleUploadFileImgs();
      setUploaded(true);
    }
  }, [selectedFiles]);

  return (
    <UploadFileContainer>
      <Card>
        <CardContent>
          <Border>
            <DropZone
              onDrop={handleDrop}
              onDragOver={(event) => event.preventDefault()}
            >
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                multiple={uploadMany}
                onChange={handleFileSelection}
                style={{ display: "none" }}
                ref={fileInputRef}
              />
              {uploaded ? (
                <CheckCircleIcon
                  sx={{
                    width: 120,
                    height: 80,
                    color: theme.color.lemon,
                  }}
                />
              ) : (
                <SystemUpdateAltIcon
                  sx={{
                    height: 80,
                    width: 80,
                    verticalAlign: "middle",
                  }}
                />
              )}
              <Typography variant="body1">
                {"Imágenes cargadas: " + amountFiles}
                {/* {customDescription} */}
              </Typography>
              <Typography variant="body1">
                <small>Únicamente se permiten archivos .jpg y .png</small>
              </Typography>
            </DropZone>


            <BtnContainer>
              <Button
                size="medium"
                color="white"
                bgColor={theme.color.navy}
                hoverColor={theme.color.space}
                firstIcon="Upload"
                firstIconBool={true}
                paddingLeft="30px"
                paddingRight="30px"
                onClick={handleOpenFileDialog}
              >
                {!uploaded ? "Subir archivo" : "Reemplazar archivo"}
              </Button>
            </BtnContainer>
          </Border>
        </CardContent>
      </Card>
      <ToastContainer />
    </UploadFileContainer>
  );
};

export default UploadFile;
